import { Check, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_OPTIONS } from '@/locales/i18n';
import { Dropdown, DropdownContent, DropdownItem, DropdownTriggerButton } from './Dropdown';

export interface LanguageSwitchProps {
  className?: string;
}

/**
 * @component LanguageSwitch
 * @description Renders a compact globe button with the current UI language that opens a menu
 *              of all shipped languages and switches the UI language on select.
 *
 * @props
 *   className (string, optional) – extra classes for the trigger button
 *
 * @notes
 *   Languages come from LANGUAGE_OPTIONS and are shown in their own language, never translated.
 *   Keyboard, outside-click and Escape handling come from the Radix-based Dropdown.
 *
 * @changelog
 *   2026-09-24 – Initial creation.
 */
export function LanguageSwitch({ className }: LanguageSwitchProps) {
  const { t, i18n } = useTranslation();
  const currentLanguageId = i18n.resolvedLanguage ?? i18n.language;
  const currentLanguage =
    LANGUAGE_OPTIONS.find((option) => option.id === currentLanguageId) ?? LANGUAGE_OPTIONS[0];

  return (
    <Dropdown>
      <DropdownTriggerButton aria-label={t('languageSwitch.changeLanguage')} className={className}>
        <Globe className="size-3.5" aria-hidden="true" />
        <span>{currentLanguage.label}</span>
      </DropdownTriggerButton>
      <DropdownContent>
        {LANGUAGE_OPTIONS.map((option) => (
          <LanguageItem
            key={option.id}
            label={option.label}
            isCurrent={option.id === currentLanguage.id}
            onSelect={() => void i18n.changeLanguage(option.id)}
          />
        ))}
      </DropdownContent>
    </Dropdown>
  );
}

interface LanguageItemProps {
  label: string;
  isCurrent: boolean;
  onSelect: () => void;
}

// One menu row; the fixed-width icon slot keeps labels aligned whether or not the check shows
function LanguageItem({ label, isCurrent, onSelect }: LanguageItemProps) {
  return (
    <DropdownItem onSelect={onSelect} aria-current={isCurrent || undefined}>
      <span className="flex size-4 items-center justify-center">
        {isCurrent && <Check className="size-4 text-primary" aria-hidden="true" />}
      </span>
      <span>{label}</span>
    </DropdownItem>
  );
}
